import { Button } from "@mui/material";
import { map } from "lodash-es";
import { useId, useState } from "react";
import { useTranslations } from "use-intl";

import { useToggleSelectAll } from "@/app/lib/hooks/useToggleSelectAll";
import { initialSortValue } from "@/app/lib/initialFlightsValues";
import { DrawerTopBarWithAction } from "@/components/Drawer/DrawerTopBar";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import type { IFlightFilters } from "@/types/interfaces";
import type { FlightsSortValue } from "@/types/types";

import { AirlinesFilter } from "./AirlinesFilter";
import FlightsSort from "./FlightsSort";
import { StopsFilter } from "./StopsFilter";

type Props = {
  defaultFilters: IFlightFilters;
  filters: IFlightFilters;
  sortValue: FlightsSortValue;
  onFiltersChange: (
    newFilters: Partial<IFlightFilters>,
    newSort: FlightsSortValue,
  ) => void;
  onClose: () => void;
};

export function FlightsFilters({
  defaultFilters,
  filters,
  sortValue,
  onFiltersChange,
  onClose,
}: Props) {
  const priceHeaderId = useId();
  const t = useTranslations();
  // Make local copy of filters
  const [localFilters, setLocalFilters] = useState<IFlightFilters>({
    ...filters,
  });
  const [currentSort, setCurrentSort] = useState<FlightsSortValue>(sortValue);

  const allAirlinesCodes = map(localFilters.airlines, "code");
  const allDefaultAirlinesCodes = map(defaultFilters.airlines, "code");

  const {
    manuallySelectedAll,
    setManuallySelectedAll,
    areAllSelected,
    toggle,
  } = useToggleSelectAll<string>({
    selected: localFilters.selectedAirlines,
    all: allAirlinesCodes,
  });

  const areAirlinesInDefaultState = areAllSelected && !manuallySelectedAll;

  return (
    <>
      <DrawerTopBarWithAction
        onCloseClick={onClose}
        title={t("flights.filters")}
        actionElement={
          <Button
            onClick={(e) => {
              setLocalFilters(defaultFilters);
              setCurrentSort(initialSortValue);
              e.currentTarget.blur();
            }}
            variant="text"
            size="small"
          >
            {t("clearFilters")}
          </Button>
        }
      />

      <div className="-mx-4 my-4 flex flex-grow flex-col gap-8 overflow-y-auto px-4">
        <div>
          <h3 className="mb-4 text-xl font-bold text-black">
            {t("flights.sort")}
          </h3>
          <FlightsSort value={currentSort} onChange={setCurrentSort} />
        </div>

        <div>
          <h3 id={priceHeaderId} className="mb-4 text-xl font-bold text-black">
            {t("hotels.filters.priceRange")}
          </h3>
          <PriceRangeSlider
            min={defaultFilters.priceRange[0]}
            max={defaultFilters.priceRange[1]}
            aria-labelledby={priceHeaderId}
            value={[localFilters.priceRange[0], localFilters.priceRange[1]]}
            onChange={(newValue) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                priceRange: newValue,
              }));
            }}
          />
        </div>

        <div>
          <h3 className="mb-4 text-xl font-bold text-black">
            {t("flights.stops")}
          </h3>
          <StopsFilter
            value={localFilters.stops}
            onChange={(newValue) => {
              // ensure that a value is selected
              if (newValue == null) {
                return;
              }

              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                stops: newValue,
              }));
            }}
          />
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="shrink-0 text-xl font-bold text-black">
              {t("flights.airlines")}
            </h3>
            {localFilters.airlines.length > 1 ? (
              <Button
                onClick={(e) => {
                  if (areAirlinesInDefaultState) {
                    setLocalFilters((prevFilters) => ({
                      ...prevFilters,
                      selectedAirlines: allAirlinesCodes,
                    }));
                    setManuallySelectedAll(true);
                  } else {
                    setLocalFilters((prevFilters) => ({
                      ...prevFilters,
                      selectedAirlines: allDefaultAirlinesCodes,
                    }));
                    setManuallySelectedAll(false);
                  }

                  // Remove focus from the button after click
                  e.currentTarget.blur();
                }}
                variant="text"
                size="small"
              >
                {areAirlinesInDefaultState ? t("selectAll") : t("clearFilter")}
              </Button>
            ) : null}
          </div>
          <AirlinesFilter
            airlines={localFilters.airlines}
            selectedAirlines={localFilters.selectedAirlines}
            userManuallySelectedAllAirlines={manuallySelectedAll}
            onSingleAirlineChange={(e, checked, airline) => {
              e.stopPropagation();
              setLocalFilters((prev) => ({
                ...prev,
                selectedAirlines: toggle(
                  prev.selectedAirlines,
                  airline.code,
                  checked,
                ),
              }));
            }}
          />
        </div>
      </div>

      <div className="-mx-4 border-t border-t-stroke-neutral-soft px-4 pt-4">
        <Button
          fullWidth
          onClick={() => onFiltersChange(localFilters, currentSort)}
          variant="contained"
          size="large"
        >
          {t("apply")}
        </Button>
      </div>
    </>
  );
}
